import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Unified Pagination Component
 *
 * Supports two modes:
 * 1. Stateless Mode: Parent manages state, component just triggers page changes
 *    - Props: activePage, itemsCountPerPage, totalItemsCount, onChange
 * 2. Stateful Mode: Component manages data slicing internally
 *    - Props: items, onChangePage, initialPage, pageSize
 *
 * Uses shadcn/Tailwind with brand colors (#313131, #f19f57, #f9f9f9)
 */
function Pagination({
    // Stateless mode
    activePage,
    itemsCountPerPage,
    totalItemsCount,
    onChange,
    pageRangeDisplayed = 5,

    // Stateful mode
    items,
    onChangePage,
    initialPage = 1,
    pageSize = 10,

    // Shared
    hideFirstLastPages = false,
    className = '',
    innerClass = '',
    itemClass = '',
    linkClass = '',
    activeClass = '',
    activeLinkClass = '',
}) {
    const [pager, setPager]               = useState({});
    const [currentPage, setCurrentPage]   = useState(initialPage);

    // Stateful mode: initialise / reset when items change
    useEffect(() => {
        if (items && items.length) {
            setPageInternal(initialPage);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    // ── Stateful-mode helpers ─────────────────────────────────────────────
    const getPager = (totalItems, page, pgSize) => {
        page   = page   || 1;
        pgSize = pgSize || 10;

        const totalPages = totalItems > 0 ? Math.ceil(totalItems / pgSize) : 1;
        let startPage, endPage;

        if (totalPages <= 10) {
            startPage = 1;
            endPage   = totalPages;
        } else if (page <= 6) {
            startPage = 1;
            endPage   = 10;
        } else if (page + 4 >= totalPages) {
            startPage = totalPages - 9;
            endPage   = totalPages;
        } else {
            startPage = page - 5;
            endPage   = page + 4;
        }

        const startIndex = (page - 1) * pgSize;
        const endIndex   = Math.min(startIndex + pgSize - 1, totalItems - 1);
        const pages      = [...Array((endPage + 1) - startPage).keys()].map(i => startPage + i);

        return { totalItems, currentPage: page, pageSize: pgSize, totalPages, startPage, endPage, startIndex, endIndex, pages };
    };

    const setPageInternal = (page) => {
        if (!items) return;

        let p = pager;
        if (page < 1 || (p.totalPages && page > p.totalPages)) return;

        const newPager      = getPager(items.length, page, pageSize);
        const pageOfItems   = items.slice(newPager.startIndex, newPager.endIndex + 1);

        setPager(newPager);
        setCurrentPage(page);

        if (onChangePage) onChangePage(pageOfItems);
    };

    // ── Stateless-mode helpers ────────────────────────────────────────────
    const getPageNumbers = () => {
        const totalPages = Math.ceil(totalItemsCount / itemsCountPerPage);
        const pages      = [];
        const halfRange  = Math.floor(pageRangeDisplayed / 2);
        let startPage    = Math.max(1, activePage - halfRange);
        let endPage      = Math.min(totalPages, activePage + halfRange);

        if (activePage <= halfRange)              endPage   = Math.min(pageRangeDisplayed, totalPages);
        if (activePage + halfRange >= totalPages) startPage = Math.max(1, totalPages - pageRangeDisplayed + 1);

        for (let i = startPage; i <= endPage; i++) pages.push(i);
        return pages;
    };

    const handlePageChange = (page) => {
        const totalPages = Math.ceil(totalItemsCount / itemsCountPerPage);
        if (page >= 1 && page <= totalPages && page !== activePage) {
            onChange(page);
        }
    };

    // ── Determine mode ────────────────────────────────────────────────────
    const isStatefulMode  = !!items;
    const isStatelessMode = !items && typeof activePage !== 'undefined';

    let pg, pgCurrentPage, pgTotalPages, pgStartIndex, pgEndIndex, pgTotalItems, pgPages;

    if (isStatefulMode) {
        pg = pager;
        if (!pg.pages || pg.pages.length <= 1) return null;
        pgCurrentPage = pg.currentPage;
        pgTotalPages  = pg.totalPages;
        pgStartIndex  = pg.startIndex;
        pgEndIndex    = pg.endIndex;
        pgTotalItems  = pg.totalItems;
        pgPages       = pg.pages;
    } else if (isStatelessMode) {
        pgTotalPages  = Math.ceil(totalItemsCount / itemsCountPerPage);
        if (pgTotalPages <= 1) return null;
        pgCurrentPage = activePage;
        pgStartIndex  = (activePage - 1) * itemsCountPerPage;
        pgEndIndex    = Math.min(pgStartIndex + itemsCountPerPage - 1, totalItemsCount - 1);
        pgTotalItems  = totalItemsCount;
        pgPages       = getPageNumbers();
    } else {
        return null;
    }

    const isFirstPage = pgCurrentPage === 1;
    const isLastPage  = pgCurrentPage === pgTotalPages;

    // Mobile: always show exactly 3 pages (or fewer if total < 3),
    // centred on current but clamped so we never fall below page 1 or above last page.
    const mobileStart = Math.min(Math.max(pgCurrentPage - 1, 1), Math.max(pgTotalPages - 2, 1));
    const mobilePages = Array.from(
        { length: Math.min(3, pgTotalPages) },
        (_, i) => mobileStart + i
    );

    const handleClick = (page) => {
        if (isStatefulMode) setPageInternal(page);
        else handlePageChange(page);
    };

    return (
        <div className={cn('flex items-center justify-end px-4 py-3 gap-3 bg-background', className)}>
            {/* Pagination Controls */}
            <div className={cn('flex items-center gap-1', innerClass)}>
                {/* First Page */}
                {!hideFirstLastPages && (
                    <Button
                        variant="outline"
                        size="icon"
                        className={cn('h-8 w-8 text-primary hover:bg-primary hover:text-white', itemClass, isFirstPage && 'opacity-50 cursor-not-allowed')}
                        onClick={() => handleClick(1)}
                        disabled={isFirstPage}
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </Button>
                )}

                {/* Previous Page */}
                <Button
                    variant="outline"
                    size="icon"
                    className={cn('h-8 w-8 text-primary hover:bg-primary hover:text-white', itemClass, isFirstPage && 'opacity-50 cursor-not-allowed')}
                    onClick={() => handleClick(pgCurrentPage - 1)}
                    disabled={isFirstPage}
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>

                {/* Desktop: full page number list */}
                {pgPages.map((page, index) => (
                    <Button
                        key={index}
                        variant={page === pgCurrentPage ? 'default' : 'outline'}
                        size="icon"
                        className={cn(
                            'hidden sm:inline-flex h-8 w-8',
                            itemClass,
                            page === pgCurrentPage
                                ? cn('bg-primary text-white hover:bg-primary hover:text-white', activeClass, activeLinkClass)
                                : 'text-primary hover:bg-primary hover:text-white',
                            linkClass
                        )}
                        onClick={() => handleClick(page)}
                    >
                        {page}
                    </Button>
                ))}

                {/* Mobile: 3 pages centred on current (prev, current, next) */}
                {mobilePages.map((page) => (
                    <Button
                        key={`m-${page}`}
                        variant={page === pgCurrentPage ? 'default' : 'outline'}
                        size="icon"
                        className={cn(
                            'sm:hidden h-8 w-8',
                            itemClass,
                            page === pgCurrentPage
                                ? cn('bg-primary text-white hover:bg-primary hover:text-white', activeClass, activeLinkClass)
                                : 'text-primary hover:bg-primary hover:text-white',
                            linkClass
                        )}
                        onClick={() => handleClick(page)}
                    >
                        {page}
                    </Button>
                ))}

                {/* Next Page */}
                <Button
                    variant="outline"
                    size="icon"
                    className={cn('h-8 w-8 text-primary hover:bg-primary hover:text-white', itemClass, isLastPage && 'opacity-50 cursor-not-allowed')}
                    onClick={() => handleClick(pgCurrentPage + 1)}
                    disabled={isLastPage}
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>

                {/* Last Page */}
                {!hideFirstLastPages && (
                    <Button
                        variant="outline"
                        size="icon"
                        className={cn('h-8 w-8 text-primary hover:bg-primary hover:text-white', itemClass, isLastPage && 'opacity-50 cursor-not-allowed')}
                        onClick={() => handleClick(pgTotalPages)}
                        disabled={isLastPage}
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}

Pagination.propTypes = {
    // Stateless mode
    activePage:          PropTypes.number,
    itemsCountPerPage:   PropTypes.number,
    totalItemsCount:     PropTypes.number,
    onChange:            PropTypes.func,

    // Stateful mode
    items:               PropTypes.array,
    onChangePage:        PropTypes.func,
    initialPage:         PropTypes.number,
    pageSize:            PropTypes.number,

    // Shared
    pageRangeDisplayed:  PropTypes.number,
    hideFirstLastPages:  PropTypes.bool,
    className:           PropTypes.string,
    innerClass:          PropTypes.string,
    itemClass:           PropTypes.string,
    linkClass:           PropTypes.string,
    activeClass:         PropTypes.string,
    activeLinkClass:     PropTypes.string,
};

export default Pagination;
export { Pagination };
